import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { getLutTexture, isLutType } from './lut-grades.js';

// 0.85 -> 0.4 (Jack, v259 tablet: "the colors are all messed up"): since v234 the
// grade follows the ARENA, not the fighter, and at 85% it re-tinted every card's
// art (HEART pinks faces, MYSTIC magenta, VOYAGE teal). 40% keeps the arena mood
// on the stage while the art reads close to its source.
const MIX_AMOUNT = 0.4;
const RAMP_SECONDS = 0.6;

const LutShader = {
  name: 'ArenaLutShader',
  uniforms: {
    tDiffuse: { value: null },
    tLut: { value: null },
    uAmount: { value: 0 },
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */`
    precision highp float;
    precision highp sampler3D;
    uniform sampler2D tDiffuse;
    uniform sampler3D tLut;
    uniform float uAmount;
    varying vec2 vUv;
    void main() {
      vec4 inputColor = texture2D(tDiffuse, vUv);
      vec3 uvw = clamp(inputColor.rgb, 0.0, 1.0) * (31.0 / 32.0) + (0.5 / 32.0);
      vec3 graded = texture(tLut, uvw).rgb;
      // OutputPass already made this display-referred sRGB. Sample and return
      // those values directly; a second transfer function would be incorrect.
      gl_FragColor = vec4(mix(inputColor.rgb, graded, uAmount), inputColor.a);
    }
  `,
};

export function createLutController(queryValue) {
  const requested = String(queryValue || '').toUpperCase();
  const mode = requested === 'OFF' ? 'off' : isLutType(requested) ? 'forced' : 'battle';
  const pass = new ShaderPass(LutShader);
  pass.name = 'ArenaLutPass';
  pass.enabled = false;

  let type = null;
  let target = 0;

  function use(typeName) {
    type = String(typeName).toUpperCase();
    pass.uniforms.tLut.value = getLutTexture(type);
  }

  if (mode === 'forced') {
    use(requested);
    pass.uniforms.uAmount.value = MIX_AMOUNT;
    target = MIX_AMOUNT;
    pass.enabled = true;
  }

  return {
    pass,
    enter(typeName) {
      if (mode !== 'battle' || !isLutType(typeName)) return;
      use(typeName);
      pass.uniforms.uAmount.value = 0;
      target = MIX_AMOUNT;
      pass.enabled = true;
    },
    exit() {
      if (mode !== 'battle') return;
      target = 0;
    },
    update(dt) {
      if (mode !== 'battle' || !pass.enabled) return;
      const amount = pass.uniforms.uAmount.value;
      const step = MIX_AMOUNT * Math.max(0, dt) / RAMP_SECONDS;
      pass.uniforms.uAmount.value = amount < target
        ? Math.min(target, amount + step)
        : Math.max(target, amount - step);
      if (target === 0 && pass.uniforms.uAmount.value === 0) {
        pass.enabled = false;
        type = null;
        pass.uniforms.tLut.value = null;
      }
    },
    debugState() {
      return {
        mode, type, enabled: pass.enabled,
        amount: +pass.uniforms.uAmount.value.toFixed(4), target,
      };
    },
  };
}
